import { progressAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

const STRIPE_TILE_PX = 45;

// A raw, slash-escaped CSS variable for `color/white/30`: inside a `{colors.…}` reference
// Chakra reads the first `/` as its color-mix opacity modifier.
const STRIPE_COLOR = 'var(--colors-color\\/white\\/30)';

const STRIPE_GRADIENT =
  `linear-gradient(45deg, ${STRIPE_COLOR} 25%, transparent 25%, transparent 50%, ` +
  `${STRIPE_COLOR} 50%, ${STRIPE_COLOR} 75%, transparent 75%, transparent)`;

const progressSlotRecipe = defineSlotRecipe({
  className: 'progress',
  slots: progressAnatomy.keys(),
  base: {
    // The radius and the clipping live on the root so a consumer-supplied
    // `borderRadius` style prop lands on the element that actually rounds.
    root: {
      overflow: 'hidden',
      width: 'full',
    },
    track: {
      position: 'relative',
      width: 'full',
    },
    range: {
      height: 'full',
      // Keeps a sliver of the range visible at very low values (Figma: min-width 4px)…
      minWidth: '4',
      // …but exactly 0 has to render nothing. A coloured stub at 0% reads as progress
      // that hasn't happened yet — wrong on a usage meter sitting next to "0 / 3,000".
      '[data-scope="progress"][data-part="root"][data-value="0"] &': {
        minWidth: 0,
      },
      transitionDuration: 'moderate',
      transitionProperty: 'width',
      transitionTimingFunction: 'ease-out',
    },
  },
  variants: {
    roundedEdges: {
      both: {
        root: { borderRadius: '2' },
      },
      end: {
        root: { borderEndEndRadius: '2', borderStartEndRadius: '2' },
      },
      none: {},
      start: {
        root: { borderEndStartRadius: '2', borderStartStartRadius: '2' },
      },
    },
    size: {
      '1': {
        track: { height: '1' },
      },
      '2': {
        track: { height: '2' },
      },
      '4': {
        track: { height: '4' },
      },
      '6': {
        track: { height: '6' },
      },
      '8': {
        track: { height: '8' },
      },
      '12': {
        track: { height: '12' },
      },
      '16': {
        track: { height: '16' },
      },
    },
    striped: {
      false: {},
      true: {
        range: {
          '--animate-from': `${STRIPE_TILE_PX}px`,
          animation: 'bg-position 1.5s linear infinite',
          backgroundImage: STRIPE_GRADIENT,
          backgroundSize: `${STRIPE_TILE_PX}px ${STRIPE_TILE_PX}px`,
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
          },
        },
      },
    },
    trackColor: {
      neutral: {
        track: { background: 'color/neutral/moderate' },
      },
      transparent: {
        track: { background: 'color/transparent' },
      },
    },
  },
  // With a neutral track the range's trailing edge stays square — the track behind it
  // carries the shape, and the root clips the leading edge. With a transparent track
  // the range *is* the visible bar, so it has to reproduce the root's rounding itself.
  compoundVariants: [
    {
      roundedEdges: 'both',
      trackColor: 'transparent',
      css: { range: { borderRadius: '2' } },
    },
    {
      roundedEdges: 'start',
      trackColor: 'transparent',
      css: { range: { borderEndStartRadius: '2', borderStartStartRadius: '2' } },
    },
    {
      roundedEdges: 'end',
      trackColor: 'transparent',
      css: { range: { borderEndEndRadius: '2', borderStartEndRadius: '2' } },
    },
  ],
  defaultVariants: {
    roundedEdges: 'both',
    size: '8',
    striped: false,
    trackColor: 'neutral',
  },
});

export default progressSlotRecipe;
